import { useDebounceValue } from "@app/hooks/useDebounce";
import {
  Button,
  Input,
  Separator,
  ToggleGroup,
  ToggleGroupItem,
} from "@shortsworks/ui";
import { Monitor, Smartphone } from "lucide-react";
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  ChangeEvent,
  Dispatch,
  SetStateAction,
} from "react";
import { DeviceType } from "@app/components/WidgetStudio";

interface EditNameButtonProps {
  name: string | null;
  onChangeName: Dispatch<SetStateAction<string | null>>;
}
const EditNameButton = ({ name, onChangeName }: EditNameButtonProps) => {
  const [isEditingName, setEditingName] = useState<boolean>(false);
  const debounceValue = useDebounceValue(name, 200);

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    return onChangeName(event.target.value);
  };

  const inputElement = useRef<HTMLDivElement>(null);
  const handleCloseClickOther = (event: MouseEvent) => {
    const target = event.target;

    if (target !== inputElement.current) {
      return setEditingName(false);
    }
  };

  useEffect(() => {
    if (inputElement.current) {
      return;
    }
    window.addEventListener("click", handleCloseClickOther);
    return window.removeEventListener("click", handleCloseClickOther);
  }, [inputElement.current]);

  return (
    <div>
      {isEditingName ? (
        <div ref={inputElement} className="flex gap-2 items-center">
          <Input
            value={name}
            onChange={handleChangeName}
            size="sm"
            placeholder="위젯 이름을 입력하세요."
            autoFocus
          />
        </div>
      ) : (
        <span
          className="cursor-text text-sm"
          onClick={() => setEditingName(true)}
        >
          {name ?? "새로운 위젯"}
        </span>
      )}
    </div>
  );
};

interface CanvasHeaderProps {
  name: string | null;
  onChangeName: Dispatch<SetStateAction<string | null>>;
  device?: DeviceType;
  onDeviceChange: (device: DeviceType) => void;
  onSaveWidget: () => void;
  onResetWidget: () => void;
  onDeleteWidget: () => void;
}

export const CanvasHeader = ({
  name,
  onChangeName,
  device = DeviceType.Desktop,
  onDeviceChange,
  onSaveWidget,
  onResetWidget,
  onDeleteWidget,
}: CanvasHeaderProps) => {
  return (
    <div className="flex w-full min-h-[52px] max-h-[52px] items-center justify-between bg-muted/20 shadow-sm px-5">
      <EditNameButton name={name} onChangeName={onChangeName} />
      <div className="flex flex-row h-full gap-2">
        <div className="space-x-1">
          <Button onClick={onSaveWidget} size="sm">
            저장
          </Button>
          <Button onClick={onResetWidget} size="sm" variant="outline">
            초기화
          </Button>
          <Button onClick={onDeleteWidget} size="sm" variant="destructive">
            삭제
          </Button>
        </div>
        <Separator orientation="vertical" />
        <ToggleGroup
          type="single"
          size="sm"
          value={device}
          onValueChange={onDeviceChange}
        >
          <ToggleGroupItem value={DeviceType.Desktop} aria-label="Desktop">
            <Monitor className="h-4 w-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value={DeviceType.Mobile} aria-label="Mobile">
            <Smartphone className="h-4 w-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  );
};
